export type Lang = 'en' | 'fr';

const COOKIE_NAME = 'ff_lang';
const STORAGE_KEY = 'ff_lang';
const MAIN_SITE_HOST = /^https:\/\/(www\.)?forcefabric\.com(\/|$)/;

const META: Record<Lang, { title: string; description: string }> = {
  en: {
    title: 'ForceFabric FinOps Assessment',
    description: 'Discover how mature your FinOps practice is. Get a personalized assessment, benchmark against peers, and receive actionable recommendations in minutes.',
  },
  fr: {
    title: 'Évaluation de la maturité FinOps | ForceFabric',
    description: "Découvrez le niveau de maturité de votre pratique FinOps. Obtenez une évaluation personnalisée, comparez-vous à vos pairs et recevez des recommandations concrètes en quelques minutes.",
  },
};

const asLang = (v: string | null | undefined): Lang | null => {
  const s = (v || '').trim().toLowerCase().slice(0, 2);
  return s === 'en' || s === 'fr' ? s : null;
};

const readCookie = (): string | null => {
  const m = document.cookie.match(new RegExp(`(?:^|; )${COOKIE_NAME}=([^;]*)`));
  return m ? decodeURIComponent(m[1]) : null;
};

const readStorage = (): string | null => {
  try { return window.localStorage.getItem(STORAGE_KEY); } catch { return null; }
};

/** Priority: ?lang= > ff_lang cookie > localStorage > browser language > 'en'. Only 'en' and 'fr' are accepted. */
export const resolveLang = (): Lang =>
  asLang(new URLSearchParams(window.location.search).get('lang')) ||
  asLang(readCookie()) ||
  asLang(readStorage()) ||
  asLang(navigator.language) ||
  'en';

/** Saves the language (cookie shared across *.forcefabric.com, localStorage), and updates <html lang>, title and meta description. */
export const persistLang = (lang: Lang): void => {
  try { window.localStorage.setItem(STORAGE_KEY, lang); } catch { /* storage unavailable */ }
  const host = window.location.hostname;
  const domain = host === 'localhost' || /^[\d.]+$/.test(host) ? '' : '; Domain=.forcefabric.com';
  const secure = window.location.protocol === 'https:' ? '; Secure' : '';
  document.cookie = `${COOKIE_NAME}=${lang}${domain}; Path=/; Max-Age=31536000; SameSite=Lax${secure}`;
  document.documentElement.lang = lang;
  document.title = META[lang].title;
  let tag = document.querySelector('meta[name="description"]');
  if (!tag) {
    tag = document.createElement('meta');
    tag.setAttribute('name', 'description');
    document.head.appendChild(tag);
  }
  tag.setAttribute('content', META[lang].description);
};

/** Removes ?lang= from the address bar so shared links do not carry it. */
export const stripLangParam = (): void => {
  const url = new URL(window.location.href);
  if (!url.searchParams.has('lang')) return;
  url.searchParams.delete('lang');
  window.history.replaceState(null, '', url.pathname + url.search + url.hash);
};

/** Main-site links use /en/ or /fr/ path prefixes. Other hosts are returned unchanged. */
export const getMainSiteUrl = (url: string, lang: Lang): string =>
  MAIN_SITE_HOST.test(url) ? url.replace(/^(https:\/\/(?:www\.)?forcefabric\.com)(?:\/(?:en|fr))?(\/|$)/, `$1/${lang}$2`) : url;
